/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../components/Button";
import { DateRangeType } from "../components/DateRangePicker";
import { ListAndFiltersPageBodyContainer, PageContainer } from "../components/layout/PageContainer";
import Section from "../components/layout/Section";
import { Loader } from "../components/Loader";
import { PaginationCounter } from "../components/Pagination/PaginationCounter";
import PaginationDetails from "../components/Pagination/PaginationDetails";
import TitleBox from "../components/TitleBox";
import Typography from "../components/Typography";
import { selectCurrentFarmerAccount } from "../features/azure-b2c/storage";
import OrdersFilter from "../features/orders/components/OrderFilter/OrderFilter";
import { NoOrders } from "../features/orders/NoOrders";
import OrderList from "../features/orders/OrderList";
import {
  searchOrderHistory,
  selectOrderHistory,
  selectOrderHistoryStatus,
} from "../features/orders/storage/orders.slice";
import { OrdersRequestCache, SalesOrder } from "../features/orders/storage/types";
import { MAX_ORDERS_PAGE_SIZE } from "../features/orders/utils/consts";
import { useAppDispatch } from "../store/rootStore";
import { TestIds } from "../testIds";
import hashObject from "../util/hash";
import { useScrollRestoration } from "../util/useScrollRestoration";

const initialDateRange: DateRangeType = {
  to: undefined,
  from: undefined,
};

const Orders = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnOrders,
    path: "/order/",
  });

  // Extract query parameters from the URL
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialSearchText = queryParams.get("searchText") ?? "";
  const initialPickupOnly = queryParams.get("pickup") ?? "false";
  const initialFromDate = queryParams.get("fromDate") ?? undefined;
  const initialToDate = queryParams.get("toDate") ?? undefined;
  const initialHideCardOrders = queryParams.get("hideCardOrders") ?? undefined;
  const initialSeedPickup = queryParams.get("seedPickup") ?? undefined;

  const [searchText, setSearchText] = React.useState<string>(initialSearchText);
  const [pickupOnly, setPickupOnly] = React.useState<boolean>(initialPickupOnly === "true");
  const [seedPickup, setSeedPickup] = React.useState<boolean>(initialSeedPickup === "true");

  const [hideCardOrders, setHideCardOrders] = React.useState<boolean>(
    initialHideCardOrders === "true"
  );
  const [dateRange, setDateRange] = React.useState<DateRangeType>({
    from: initialFromDate ? new Date(initialFromDate) : undefined,
    to: initialToDate ? new Date(initialToDate) : undefined,
  });

  const isFilterApplied = React.useMemo(() => {
    return searchText !== "" || dateRange.to !== undefined || dateRange.from !== undefined;
  }, [searchText, dateRange]);

  const clearFilters = () => {
    setSearchText("");
    setDateRange(initialDateRange);
    setPickupOnly(false);
    setHideCardOrders(false);
    setSeedPickup(false);
  };

  const deliveryMode = () => {
    if (pickupOnly) {
      return "Pickup";
    }

    if (hideCardOrders) {
      return "!Card";
    }

    if (seedPickup) {
      return "WarePickup";
    }
    return undefined;
  };

  const { hash, obj } = React.useMemo(() => {
    const obj = {
      a: dateRange.from,
      b: dateRange.to,
      c: searchText,
      d: deliveryMode(),
      e: account?.accountNumber,
    };
    return {
      hash: hashObject(obj),
      obj: obj,
    };
  }, [dateRange, searchText, pickupOnly, hideCardOrders, seedPickup, account]);

  const requestStatus: OrdersRequestCache | null = useSelector(selectOrderHistoryStatus(hash));
  const orders: SalesOrder[] = useSelector(selectOrderHistory(hash));

  const [timer, setTimer] = React.useState<NodeJS.Timeout>();

  const loadOrders = () => {
    if (account) {
      if (requestStatus?.status !== "pending" && !requestStatus?.last) {
        clearTimeout(timer);
        setTimer(
          setTimeout(() => {
            dispatch(
              searchOrderHistory({
                accountNumber: account.accountNumber,
                // Always fetch from the start on filter/account change; skip: orders.length would use
                // persisted list length and skip past new orders (see orders slice: skip === 0 replaces).
                skip: 0,
                top: MAX_ORDERS_PAGE_SIZE,
                startDateTime: dateRange.from,
                endDateTime: dateRange.to,
                salesId: searchText,
                deliveryMode: deliveryMode(),
              })
            );
          }, 1000)
        );
      }
    }
  };

  useEffect(() => {
    if (hash !== "" && !!account && obj.e === account?.accountNumber) {
      loadOrders();
    } else if (obj.e === account?.accountNumber) {
      loadMore();
    }
  }, [hash]);

  const loadMore = () => {
    if (account) {
      if (requestStatus?.status !== "pending" && !requestStatus?.last) {
        dispatch(
          searchOrderHistory({
            accountNumber: account.accountNumber,
            skip: orders.length,
            top: MAX_ORDERS_PAGE_SIZE,
            startDateTime: dateRange.from,
            endDateTime: dateRange.to,
            salesId: searchText,
            deliveryMode: deliveryMode(),
          })
        );
      }
    }
  };

  const paginationCounter = React.useMemo(() => {
    if (
      orders.length === 0 &&
      requestStatus?.status !== "rejected" &&
      requestStatus?.status !== "fulfilled"
    ) {
      return null;
    }
    return (
      <PaginationDetails
        showClear={isFilterApplied}
        onClear={clearFilters}
        paginationCounter={
          <PaginationCounter
            data-testid={TestIds.feat.orders.list.paginationCounter}
            count={orders.length}
            currentPage={1}
            itemsPerPage={orders.length}
            label="orders"
          />
        }
      />
    );
  }, [requestStatus, orders, isFilterApplied, clearFilters]);

  useEffect(() => {
    const newQueryParams = new URLSearchParams();
    if (searchText) {
      newQueryParams.set("searchText", searchText);
    }
    if (dateRange.from) {
      newQueryParams.set("fromDate", dateRange.from.toISOString());
    }
    if (dateRange.to) {
      newQueryParams.set("toDate", dateRange.to.toISOString());
    }
    if (pickupOnly) {
      newQueryParams.set("pickup", pickupOnly.toString());
    }
    if (hideCardOrders) {
      newQueryParams.set("hideCardOrders", hideCardOrders.toString());
    }
    if (seedPickup) {
      newQueryParams.set("seedPickup", seedPickup.toString());
    }
    navigate({ search: newQueryParams.toString() }, { replace: true });
  }, [searchText, dateRange, pickupOnly, hideCardOrders, seedPickup]);

  const footer = React.useMemo(() => {
    if (!requestStatus || requestStatus?.status === "pending") {
      return <Loader title="Loading your orders" type="inline-flex" />;
    }

    if (requestStatus?.last) {
      if (orders.length > 0) {
        return (
          <Typography variant="body1" color={"grey700"}>
            No more orders to display
          </Typography>
        );
      } else {
        return (
          <Box width={"100%"} sx={{ marginY: -2 }}>
            <Typography variant="body1" color={"grey800"}>
              There are no orders matching your search. Try adjusting your filters to broaden your
              search.
            </Typography>
          </Box>
        );
      }
    }

    if (requestStatus?.status === "rejected") {
      const center = orders?.length > 0;
      return (
        <Box
          width={"100%"}
          alignItems={center ? "center" : "left"}
          display="flex"
          flexDirection={"column"}
          mt={-2.5}
          gap={1}
        >
          <Typography
            variant="body1"
            color={"error700"}
            align={center ? "center" : "left"}
            data-testid={TestIds.feat.orders.list.loadMoreError}
          >
            Oops! Something went wrong loading your latest orders.
            <br /> Please try again
          </Typography>

          <Button
            variant={center ? "text" : "contained"}
            onClick={loadMore}
            sx={{ flexShrink: 1, width: "min-content", whiteSpace: "nowrap" }}
            data-testid={TestIds.feat.orders.list.loadMore}
          >
            {center ? "Load more orders" : "Load orders"}
          </Button>
        </Box>
      );
    }

    return (
      <Button variant="text" onClick={loadMore} data-testid={TestIds.feat.orders.list.loadMore}>
        Load more orders
      </Button>
    );
  }, [requestStatus, orders, loadMore]);

  if (requestStatus?.status === "fulfilled" && !isFilterApplied && orders.length === 0) {
    return (
      <PageContainer data-testid={TestIds.pages.orders} sx={{ pb: "100px" }}>
        <Section pb={1} mb={3}>
          <TitleBox display="Your orders" />
        </Section>
        <NoOrders
          bodyText={
            "You do not currently have any orders to display. Your order will be displayed here once you place an order with Farmlands."
          }
          bodyPropsSx={{ marginBottom: 3 }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid={TestIds.pages.orders} sx={{ pb: "100px" }}>
      <Section mb={1}>
        <TitleBox display="Your orders" />
        <Box flex={"0 0 %50"} display="relative" pb={2}>
          <OrdersFilter
            dateRange={{
              value: dateRange,
              setValue: setDateRange,
            }}
            hideFarmlandsCardOrders={{
              value: hideCardOrders,
              setValue: setHideCardOrders,
            }}
            pickupOnly={{
              value: pickupOnly,
              setValue: setPickupOnly,
            }}
            seedPickup={{
              value: seedPickup,
              setValue: setSeedPickup,
            }}
            searchText={{
              value: searchText,
              setValue: setSearchText,
            }}
          />
        </Box>
      </Section>

      <ListAndFiltersPageBodyContainer>
        <Box flex={1}>
          {paginationCounter}
          <OrderList data={orders} />

          <Box marginY={4} width={"100%"} display="inline-flex" justifyContent={"center"}>
            {footer}
          </Box>
        </Box>
      </ListAndFiltersPageBodyContainer>
    </PageContainer>
  );
};

export default Orders;
